import { useEffect, useRef, useState, Fragment } from "react";
import { Checkbox } from '@/shared/components/Checkbox'
import { ChevronDown, ChevronRight } from "lucide-react";

/**
 * ===== Tipos UI =====
 * Estructura:
 *  - systems (tabs)
 *    - modules (tabs)
 *      - resources (filas)
 *        - permissions (checkbox por acción)
 */

export interface ResourceItem {
  key: string; // proveedores.administracion.documentos_proveedor
  name: string; // documentos_proveedor (o nombre bonito)
  realName?: string;
  permissions: Record<string, boolean>; // { consultar: true, crear: false, ... }
}

export interface ModuleItem {
  key: string; // proveedores.administracion
  name: string; // administracion
  resources: ResourceItem[];
}

export interface PermissionSystemSection {
  id?: string;
  key: string; // proveedores | rh | almacen | etc
  title: string; // Proveedores | RH | etc
  modules: ModuleItem[];
}

interface PermissionsTableProps {
  data: PermissionSystemSection[];
  onChange?: (data: PermissionSystemSection[]) => void;
  readOnly?: boolean;
}

// Encuentra la clave real de una accion (p.ej. "ver") dentro de las
// permissions de un recurso, sin asumir un nombre exacto entre modulos.
const findActionKey = (
  permissions: Record<string, boolean>,
  candidates: string[],
): string | undefined =>
  Object.keys(permissions).find((key) =>
    candidates.includes(key.toLowerCase()),
  );

// El recurso de herramientas (almacen.gestionar.herramientas) es el ÚNICO con
// dos permisos de vista: "ver" (todas) y "ver_asignadas". Crear/Editar no deben
// forzar "ver todas", pero tampoco pueden quedarse sin ninguna vista: el rol se
// guarda y el usuario no tiene por dónde entrar al módulo. Ahí se garantiza la
// vista mínima (asignadas) y el admin decide si la sube a todas.
const isHerramientasResource = (key?: string): boolean =>
  (key || "").toLowerCase().split(".").pop() === "herramientas";

const findViewKeys = (permissions: Record<string, boolean>) => ({
  verKey: findActionKey(permissions, ["ver"]),
  verAsignadasKey: findActionKey(permissions, ["ver_asignadas"]),
});

export const PermissionsTable = ({
  data,
  onChange,
  readOnly = false,
}: PermissionsTableProps) => {
  const [systems, setSystems] = useState<PermissionSystemSection[]>([]);
  const [activeSystemTab, setActiveSystemTab] = useState(0);
  const [activeModuleTab, setActiveModuleTab] = useState(0);

  // openRows por "systemTabIndex + moduleTabIndex + resourceIndex"
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});

  const RH_MODULE_ORDER = [
    "inicio",
    "solicitudes_personal",
    "gestion_solicitudes",
    "reclutamiento_candidatos",
    "entrevistas",
    "entrevistas_preseleccion",
    "empleados_preregistro",
    "empleados_expedientes",
  ];

  const normalizeModuleName = (mod: ModuleItem) => {
    const fromName = (mod.name || "").toLowerCase().trim();
    if (fromName) return fromName;

    const fromKey = (mod.key || "").split(".").pop() || "";
    return fromKey.toLowerCase().trim();
  };

  const sortModulesForSystem = (system: PermissionSystemSection) => {
    if ((system.key || "").toLowerCase() !== "rh") {
      return system.modules;
    }

    const withIndex = system.modules.map((mod, idx) => ({ mod, idx }));
    withIndex.sort((a, b) => {
      const aName = normalizeModuleName(a.mod);
      const bName = normalizeModuleName(b.mod);
      const aPos = RH_MODULE_ORDER.indexOf(aName);
      const bPos = RH_MODULE_ORDER.indexOf(bName);

      if (aPos === -1 && bPos === -1) return a.idx - b.idx;
      if (aPos === -1) return 1;
      if (bPos === -1) return -1;
      return aPos - bPos;
    });

    return withIndex.map((x) => x.mod);
  };

  useEffect(() => {
    const normalized = data.map((system) => ({
      ...system,
      modules: sortModulesForSystem(system),
    }));
    setSystems(normalized);

    setActiveSystemTab((prev) => (prev >= data.length ? 0 : prev));
  }, [data]);

  const prettify = (value: string) => {
    if (value == "Base") {
      return "Acciones base";
    } else if (value == "Especial") {
      return "Acciones especiales";
    } else {
      const withSpaces = value.replace(/_/g, " ").trim();
      if (!withSpaces) return "";
      const lower = withSpaces.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    }
  };

  const ACTION_LABELS: Record<string, string> = {
    read: "Consultar",
    create: "Crear",
    update: "Editar",
    edit: "Editar",
    delete: "Eliminar",
    manage: "Administrar",
    approve: "Aprobar",
    reject: "Rechazar",
    pre_approve: "Pre aprobar",
    change_status: "Mover en el proceso",
    capturar_feedback: "Registrar resultado / Feedback",
    agendar: "Agendar",
    schedule: "Agendar",
    publish: "Publicar",
    activate: "Activar",
    deactivate: "Desactivar",
    assign: "Asignar",
  };

  const prettifyAction = (value: string, resourceKey?: string) => {
    const fullPermissionKey = `${resourceKey || ""}.${value}`.toLowerCase();
    if (fullPermissionKey === "rh.entrevistas.base.create") {
      return "Crear y agendar";
    }

    return ACTION_LABELS[value.toLowerCase()] ?? prettify(value);
  };

  const activeSystem = systems[activeSystemTab];
  const activeModule = activeSystem?.modules?.[activeModuleTab];

  const safeModulesCount = activeSystem?.modules?.length ?? 0;
  useEffect(() => {
    if (activeModuleTab >= safeModulesCount) setActiveModuleTab(0);
  }, [safeModulesCount, activeModuleTab]);

  const openKey = (resourceIndex: number) =>
    `${activeSystemTab}:${activeModuleTab}:${resourceIndex}`;

  const handlePermissionChange = (
    resourceIndex: number,
    action: string,
    value: boolean,
  ) => {
    if (readOnly) return;
    if (!activeSystem || !activeModule) return;

    const updated = structuredClone(systems);
    const permissions =
      updated[activeSystemTab].modules[activeModuleTab].resources[resourceIndex]
        .permissions;

    permissions[action] = value;

    // Si se activa cualquier permiso, forzar tambien permiso de lectura/consulta.
    if (value) {
      const readActionKey =
        Object.keys(permissions).find((key) => key.toLowerCase() === "read") ||
        Object.keys(permissions).find((key) => key.toLowerCase() === "consultar") ||
        Object.keys(permissions).find((key) => key.toLowerCase() === "consulta") ||
        Object.keys(permissions).find((key) => key.toLowerCase().includes("read")) ||
        Object.keys(permissions).find((key) => key.toLowerCase().includes("consult"));

      if (readActionKey) {
        permissions[readActionKey] = true;
      }
    }

    // Dependencias Crear -> Ver + Editar, Editar -> Ver (independiente de
    // Eliminar/Dar de baja, que no deben arrastrar nada). "ver" es la clave
    // usada por Almacén y no la reconocia la regla generica de arriba.
    const resourceKey =
      updated[activeSystemTab].modules[activeModuleTab].resources[resourceIndex]
        .key;
    if (value) {
      const { verKey, verAsignadasKey } = findViewKeys(permissions);
      const editarKey = findActionKey(permissions, ["editar", "update", "edit"]);
      const crearKey = findActionKey(permissions, ["crear", "create"]);
      const esCrear = Boolean(crearKey) && action === crearKey;
      const esEditar = Boolean(editarKey) && action === editarKey;

      if (isHerramientasResource(resourceKey)) {
        // Crear sigue arrastrando Editar. Para la vista solo se garantiza que
        // haya alguna: si el admin ya eligió una no se le cambia, y si no hay
        // ninguna se marca la mínima (asignadas).
        if (esCrear && editarKey) permissions[editarKey] = true;

        if (esCrear || esEditar) {
          const tieneAlgunaVista =
            (verKey ? permissions[verKey] : false) ||
            (verAsignadasKey ? permissions[verAsignadasKey] : false);
          const vistaMinimaKey = verAsignadasKey ?? verKey;

          if (!tieneAlgunaVista && vistaMinimaKey) {
            permissions[vistaMinimaKey] = true;
          }
        }
      } else {
        if (esCrear) {
          if (verKey) permissions[verKey] = true;
          if (editarKey) permissions[editarKey] = true;
        }
        if (esEditar) {
          if (verKey) permissions[verKey] = true;
        }
      }
    }

    setSystems(updated);
    onChange?.(updated);
  };

  const handleResourceToggle = (resourceIndex: number, value: boolean) => {
    if (readOnly) return;
    if (!activeSystem || !activeModule) return;

    const updated = structuredClone(systems);

    const perms =
      updated[activeSystemTab].modules[activeModuleTab].resources[resourceIndex]
        .permissions;

    for (const key of Object.keys(perms)) {
      perms[key] = value;
    }

    setSystems(updated);
    onChange?.(updated);
  };

  const getResourceCheckState = (resource: ResourceItem) => {
    const values = Object.values(resource.permissions);
    const total = values.length;
    const checkedCount = values.filter(Boolean).length;

    return {
      allChecked: total > 0 && checkedCount === total,
      noneChecked: checkedCount === 0,
      indeterminate: checkedCount > 0 && checkedCount < total,
    };
  };

  const ResourceSelectAllCheckbox = ({
    resource,
    resourceIndex,
  }: {
    resource: ResourceItem;
    resourceIndex: number;
  }) => {
    const ref = useRef<HTMLInputElement | null>(null);
    const state = getResourceCheckState(resource);

    useEffect(() => {
      if (ref.current) ref.current.indeterminate = state.indeterminate;
    }, [state.indeterminate]);

    return (
      <Checkbox
        ref={ref}
        checked={state.allChecked}
        disabled={readOnly}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => handleResourceToggle(resourceIndex, e.target.checked)}
      />
    );
  };

  if (!systems.length) {
    return (
      <div className="mt-8 text-sm text-gray-500">
        No hay permisos para mostrar.
      </div>
    );
  }

  if (!activeSystem || !activeModule) {
    return (
      <div className="mt-8 text-sm text-gray-500">
        No hay módulos o secciones para mostrar.
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between">
        <h2 className="text-lg font-semibold text-[#1E1E1F] mb-6">
          Permisos por módulo
        </h2>

        {/* ===== Tabs SISTEMAS ===== */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {systems.map((sys, index) => (
            <button
              key={`${sys.key}-${index}`}
              type="button"
              onClick={() => {
                setActiveSystemTab(index);
                setActiveModuleTab(0);
                setOpenRows({});
              }}
              className={`px-3 py-1 rounded-md text-xs transition-colors ${
                activeSystemTab === index
                  ? "bg-[#315FB9] text-white"
                  : "bg-white text-[#777777] border border-[#E5E7EBx]"
              }`}
            >
              {sys.title}
            </button>
          ))}
        </div>
      </div>

      {/* ===== Tabs MÓDULOS (administracion/fiscal/inicio/etc) ===== */}
      <div className="px-2">
        <div className="flex gap-6 text-sm flex-wrap">
          {activeSystem.modules.map((mod, index) => (
            <button
              key={`${mod.key}-${index}`}
              type="button"
              onClick={() => {
                setActiveModuleTab(index);
                setOpenRows({});
              }}
              className={`py-2 border-b-2 transition-colors ${
                activeModuleTab === index
                  ? "border-[#477FFA] font-semibold"
                  : "border-transparent font-medium"
              }`}
            >
              {prettify(mod.name)}
            </button>
          ))}
        </div>
      </div>

      {/* ===== Tabla RESOURCES ===== */}
      <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB] mt-6">
        <table className="min-w-full divide-y divide-[#E5E7EB]">
          <tbody className="bg-white divide-y divide-[#E1E1E1]">
            {activeModule.resources.map((resource, resourceIndex) => {
              const open = !!openRows[openKey(resourceIndex)];

              return (
                <Fragment key={`${resource.key}-${resourceIndex}`}>
                  {/* Resource row */}
                  <tr
                    className="bg-[#FAF9FF] cursor-pointer hover:bg-[#F4F7FB] transition-colors"
                    onClick={() =>
                      setOpenRows((prev) => ({
                        ...prev,
                        [openKey(resourceIndex)]: !open,
                      }))
                    }
                  >
                    <td className="p-4 font-semibold">
                      <div className="flex items-center gap-3">
                        <ResourceSelectAllCheckbox
                          resource={resource}
                          resourceIndex={resourceIndex}
                        />
                        <span>{prettify(resource.name)}</span>
                      </div>
                    </td>

                    <td className="p-4 text-right w-10">
                      {open ? (
                        <ChevronDown className="w-4 h-4 inline" />
                      ) : (
                        <ChevronRight className="w-4 h-4 inline" />
                      )}
                    </td>
                  </tr>

                  {/* Permissions */}
                  {open && (() => {
                    const { verKey, verAsignadasKey } = findViewKeys(
                      resource.permissions,
                    );
                    const editarKey = findActionKey(resource.permissions, [
                      "editar",
                      "update",
                      "edit",
                    ]);
                    const crearKey = findActionKey(resource.permissions, [
                      "crear",
                      "create",
                    ]);
                    const crearActive = crearKey
                      ? resource.permissions[crearKey]
                      : false;
                    const editarActive = editarKey
                      ? resource.permissions[editarKey]
                      : false;
                    // En herramientas el bloqueo cae sobre la última vista que
                    // quede activa, no sobre "ver todas": mientras haya dos
                    // marcadas cualquiera se puede quitar.
                    const vistasActivas = [
                      verKey ? resource.permissions[verKey] : false,
                      verAsignadasKey
                        ? resource.permissions[verAsignadasKey]
                        : false,
                    ].filter(Boolean).length;

                    return (
                      <tr className="bg-white">
                        <td colSpan={2} className="p-0">
                          {Object.entries(resource.permissions).map(
                            ([action, enabled]) => {
                              // Ver no se puede desmarcar mientras Crear o
                              // Editar esten activos; Editar no se puede
                              // desmarcar mientras Crear este activo. En
                              // herramientas el candado protege la última vista
                              // que quede, sea "ver todas" o "ver asignadas".
                              const esUltimaVista =
                                (action === verKey ||
                                  action === verAsignadasKey) &&
                                enabled &&
                                vistasActivas === 1;

                              const lockedOn = isHerramientasResource(
                                resource.key,
                              )
                                ? (esUltimaVista &&
                                    (crearActive || editarActive)) ||
                                  (action === editarKey && crearActive)
                                : (action === verKey &&
                                    (crearActive || editarActive)) ||
                                  (action === editarKey && crearActive);

                              return (
                                <div
                                  key={`${resource.key}.${action}`}
                                  className="flex items-center gap-3 p-4 border-t"
                                >
                                  <Checkbox
                                    checked={enabled}
                                    disabled={readOnly || lockedOn}
                                    onChange={(e) =>
                                      handlePermissionChange(
                                        resourceIndex,
                                        action,
                                        e.target.checked,
                                      )
                                    }
                                  />
                                  <span>
                                    {prettifyAction(action, resource.key)}
                                  </span>
                                </div>
                              );
                            },
                          )}
                        </td>
                      </tr>
                    );
                  })()}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
